import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import SEO from '../../components/SEO';
import {
  Building2,
  Calendar,
  Clock,
  ArrowLeft,
  FileSearch,
  CheckCircle,
  Database,
  Scale,
  Globe,
  Eye,
  AlertTriangle,
  Download
} from 'lucide-react';

export default function CADAOpenDataArticle() {
  return (
    <>
      <SEO
        title="CADA et Open Data des Marchés Publics 2026 : Vos Droits d'Accès aux Documents"
        description="Comment exercer votre droit CADA pour accéder aux documents des marchés publics : DECP open data, BOAMP, attributions, mémoires concurrents. Guide juridique pratique 2026."
        path="/blog/cada-open-data-marches-publics"
      />
      <div className="min-h-screen bg-white">
        <header className="bg-gradient-to-r from-blue-900 via-cyan-900 to-slate-900 text-white py-4">
          <div className="container mx-auto px-4 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center">
                <Building2 className="w-6 h-6" />
              </div>
              <span className="font-bold text-xl">Mon DCE Express</span>
            </Link>
            <div className="flex gap-4">
              <Link to="/login"><Button variant="ghost" className="text-white hover:bg-white/10">Connexion</Button></Link>
              <Link to="/register"><Button className="bg-white text-blue-900 hover:bg-slate-100">S'inscrire</Button></Link>
            </div>
          </div>
        </header>

        <article className="py-12">
          <div className="container mx-auto px-4 max-w-4xl">
            <Link to="/blog" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-8">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full text-sm font-medium bg-blue-100 text-blue-700">Juridique &amp; Transparence</span>
                <span className="px-2 py-1 rounded-full text-xs font-bold bg-gradient-to-r from-blue-600 to-cyan-600 text-white">NOUVEAU</span>
              </div>
              <h1 className="text-4xl font-bold text-slate-900 mb-4" data-testid="article-title">
                CADA et Open Data des Marchés Publics 2026 : Vos Droits d'Accès aux Documents Administratifs
              </h1>
              <div className="flex items-center gap-6 text-slate-500">
                <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />Mai 2026</span>
                <span className="flex items-center gap-2"><Clock className="w-4 h-4" />12 min de lecture</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-500 to-cyan-600 rounded-2xl p-12 mb-12 text-white text-center">
              <FileSearch className="w-20 h-20 mx-auto mb-4 opacity-90" />
              <p className="text-xl font-medium">CADA · DECP · BOAMP · data.gouv.fr</p>
              <p className="text-blue-100">Tout citoyen, toute entreprise peut accéder aux documents des marchés publics</p>
            </div>

            <div className="prose prose-lg max-w-none">
              <p className="text-xl text-slate-600 leading-relaxed mb-8">
                La <strong>Commission d'Accès aux Documents Administratifs (CADA)</strong>, instituée par la loi
                du 17 juillet 1978, garantit à toute personne le droit d'accéder aux documents produits ou détenus
                par les administrations publiques. Couplée à la <strong>Loi pour une République numérique</strong>{' '}
                de 2016 et au principe d'<strong>Open Data par défaut</strong>, elle constitue un outil
                puissant — et trop méconnu — pour les entreprises qui répondent aux marchés publics.
              </p>

              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded-r-lg mb-8">
                <div className="flex items-start gap-3">
                  <Scale className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-blue-900 mb-2">Le cadre juridique 2026</h4>
                    <ul className="text-blue-800 space-y-1 text-sm">
                      <li>• <strong>Loi du 17 juillet 1978</strong> : droit d'accès aux documents administratifs (CADA)</li>
                      <li>• <strong>Code des relations entre le public et l'administration (CRPA)</strong> articles L.300-1 et suivants</li>
                      <li>• <strong>Loi Lemaire 2016</strong> : open data par défaut pour les administrations</li>
                      <li>• <strong>Décret DECP 2017</strong> : publication obligatoire des données essentielles des marchés &gt; 40 000 € HT</li>
                      <li>• <strong>Code de la commande publique</strong> articles L.2196-1 et R.2196-1</li>
                    </ul>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Globe className="w-7 h-7 text-blue-600" />
                1. L'Open Data par défaut : ce qui est déjà public
              </h2>

              <p>
                Avant même de saisir la CADA, sachez que de nombreuses données sont déjà publiées en{' '}
                <strong>open data</strong> et accessibles gratuitement. À utiliser en priorité :
              </p>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Database className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-3">DECP (data.economie.gouv.fr)</h4>
                  <p className="text-slate-600 text-sm mb-2">
                    Données Essentielles de la Commande Publique. Tous les marchés &gt; 40 K€ HT depuis 2017 :
                    objet, titulaire, montant, durée, acheteur, date d'attribution.
                  </p>
                  <p className="text-xs text-blue-700"><strong>Format</strong> : CSV, JSON, API REST</p>
                </div>
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <FileSearch className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-3">BOAMP & TED Europa</h4>
                  <p className="text-slate-600 text-sm mb-2">
                    Avis d'appel public à concurrence, avis d'attribution, modifications, résiliations.
                    Consultables gratuitement, exportables en XML/RSS.
                  </p>
                  <p className="text-xs text-blue-700"><strong>Volume</strong> : 200 000+ avis/an en France</p>
                </div>
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-3">data.gouv.fr</h4>
                  <p className="text-slate-600 text-sm mb-2">
                    Portail national : budgets locaux, subventions, marchés publics communaux, conventions.
                    Plus de <strong>40 000 jeux de données</strong> publiés par les administrations.
                  </p>
                </div>
                <div className="bg-white border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <Eye className="w-6 h-6 text-blue-600" />
                  </div>
                  <h4 className="font-bold text-slate-900 mb-3">PLACE & achatpublic.com</h4>
                  <p className="text-slate-600 text-sm mb-2">
                    Profil d'acheteur de l'État. Avis d'attribution avec montants détaillés, classement
                    parfois publié (non obligatoire).
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Scale className="w-7 h-7 text-blue-600" />
                2. La procédure CADA : quand l'open data ne suffit pas
              </h2>

              <p>
                Si vous voulez accéder à un document précis non publié (CCAP signé, mémoire technique du concurrent
                attributaire, rapport d'analyse des offres, etc.), il faut saisir le <strong>responsable d'accès aux
                documents administratifs</strong> de l'organisme acheteur.
              </p>

              <div className="bg-slate-100 rounded-xl p-6 my-8">
                <h4 className="font-bold text-slate-900 mb-4">Procédure CADA en 4 étapes</h4>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center flex-shrink-0">1</div>
                    <div>
                      <h5 className="font-bold text-slate-900">Demande directe à l'administration</h5>
                      <p className="text-slate-600 text-sm">Envoi d'un courrier ou e-mail à la PRADA (Personne Responsable de l'Accès aux Documents Administratifs) de l'acheteur. Préciser le document souhaité et le format.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center flex-shrink-0">2</div>
                    <div>
                      <h5 className="font-bold text-slate-900">Délai de réponse : 1 mois</h5>
                      <p className="text-slate-600 text-sm">L'administration doit répondre dans un délai d'<strong>1 mois</strong>. Le silence vaut refus tacite après ce délai.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center flex-shrink-0">3</div>
                    <div>
                      <h5 className="font-bold text-slate-900">Saisine de la CADA</h5>
                      <p className="text-slate-600 text-sm">En cas de refus ou de silence, saisir la CADA via le formulaire en ligne sur <a href="https://www.cada.fr" target="_blank" rel="noopener noreferrer" className="text-blue-700 underline">cada.fr</a> dans un délai de 2 mois. Procédure <strong>gratuite</strong>.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white font-bold flex items-center justify-center flex-shrink-0">4</div>
                    <div>
                      <h5 className="font-bold text-slate-900">Avis de la CADA et recours</h5>
                      <p className="text-slate-600 text-sm">La CADA rend un avis dans un délai d'<strong>1 mois</strong>. Si l'administration persiste dans son refus malgré un avis favorable, recours au <strong>Tribunal administratif</strong> dans les 2 mois.</p>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6 flex items-center gap-3">
                <Eye className="w-7 h-7 text-blue-600" />
                3. Quels documents pouvez-vous obtenir ?
              </h2>

              <div className="grid md:grid-cols-2 gap-6 my-8">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                  <h4 className="font-bold text-emerald-900 mb-3">✅ Communicables</h4>
                  <ul className="text-emerald-800 text-sm space-y-2">
                    <li>• Avis d'appel public à la concurrence (AAPC)</li>
                    <li>• Règlement de consultation (RC)</li>
                    <li>• CCTP, CCAP signés</li>
                    <li>• Contrat / acte d'engagement signé (après attribution)</li>
                    <li>• Montant total du marché, identité du titulaire</li>
                    <li>• Rapport d'analyse des offres (anonymisé)</li>
                    <li>• Décompte général et définitif (DGD)</li>
                    <li>• Avenants signés</li>
                  </ul>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h4 className="font-bold text-red-900 mb-3">❌ Non communicables / Occultations</h4>
                  <ul className="text-red-800 text-sm space-y-2">
                    <li>• Mémoires techniques détaillés (secret des affaires)</li>
                    <li>• Détail des prix unitaires (DPGF) après attribution</li>
                    <li>• Données financières / fiscales d'une entreprise</li>
                    <li>• Documents préparatoires non finalisés</li>
                    <li>• Données à caractère personnel (CV nominatifs, etc.)</li>
                    <li>• Secrets industriels et commerciaux</li>
                  </ul>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 my-8">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-bold text-amber-900 mb-2">Le principe d'occultation</h4>
                    <p className="text-amber-800 text-sm">
                      L'administration doit communiquer les documents en <strong>occultant</strong> (caviardant) les
                      mentions protégées (secret des affaires, données personnelles). Elle ne peut pas refuser
                      l'intégralité d'un document sous prétexte qu'il contient quelques mentions confidentielles.
                    </p>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">
                4. Cas pratiques pour une entreprise candidate
              </h2>

              <div className="space-y-4 my-8">
                <div className="bg-white border-l-4 border-blue-500 p-5 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 mb-2">Cas n°1 — Connaître le montant remporté par votre concurrent</h4>
                  <p className="text-slate-600 text-sm">
                    Direct via DECP (open data) en 30 secondes : recherche par acheteur + objet + date. Montant
                    total HT toujours publié pour les marchés &gt; 40 K€.
                  </p>
                </div>
                <div className="bg-white border-l-4 border-blue-500 p-5 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 mb-2">Cas n°2 — Demander le rapport d'analyse des offres</h4>
                  <p className="text-slate-600 text-sm">
                    Procédure CADA. L'acheteur doit fournir le rapport <strong>anonymisé</strong>, ce qui permet
                    de comprendre les forces/faiblesses pondérées par le jury. Utile après un échec pour s'améliorer.
                  </p>
                </div>
                <div className="bg-white border-l-4 border-blue-500 p-5 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 mb-2">Cas n°3 — Analyser un historique d'attribution pour cibler un acheteur</h4>
                  <p className="text-slate-600 text-sm">
                    Export DECP par SIRET d'acheteur → analyse des 3 dernières années : titulaires habituels,
                    fourchettes de prix, fréquences de renouvellement, allotissement préféré. Permet de calibrer
                    une offre alignée.
                  </p>
                </div>
                <div className="bg-white border-l-4 border-blue-500 p-5 rounded-r-lg">
                  <h4 className="font-bold text-slate-900 mb-2">Cas n°4 — Obtenir un CCTP d'un marché passé pour préparer un futur AO</h4>
                  <p className="text-slate-600 text-sm">
                    Le CCTP signé est communicable. Permet d'anticiper la prochaine consultation si vous savez
                    qu'un marché arrive à échéance. Demande directe à la collectivité, sans saisir la CADA.
                  </p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">5. Bonnes pratiques de demande</h2>

              <ul className="space-y-3 my-6">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span><strong>Soyez précis</strong> : indiquer le numéro du marché, la date d'attribution, l'objet exact.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span><strong>Choisissez le bon format</strong> : si possible, demandez le format électronique (.pdf, .csv, .xlsx) plutôt que papier.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span><strong>Demande gratuite</strong> jusqu'à un coût matériel raisonnable. Au-delà, frais de reproduction possibles (en général &lt; 30 €).</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span><strong>Conservez les preuves d'envoi</strong> (accusé de réception, e-mail horodaté) pour pouvoir saisir la CADA en cas de refus.</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                  <span><strong>Ne pas saisir la CADA prématurément</strong> : il faut une décision de refus (explicite ou tacite) avant de saisir.</span>
                </li>
              </ul>

              <h2 className="text-2xl font-bold text-slate-900 mt-12 mb-6">Conclusion</h2>

              <p>
                La transparence des marchés publics est un <strong>droit consacré par la loi</strong>. Entre l'open
                data en accès libre et la procédure CADA, les entreprises disposent d'outils puissants pour
                comprendre leur marché, analyser la concurrence et améliorer leur taux de conversion. Trop peu de
                PME les utilisent. C'est pourtant un <strong>levier stratégique majeur</strong>, gratuit, et qui ne
                demande qu'une bonne méthode.
              </p>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 my-8">
                <h4 className="font-bold text-blue-900 mb-3">Mon DCE Express intègre l'open data DECP</h4>
                <p className="text-blue-800 text-sm">
                  Notre plateforme exploite directement les données ouvertes DECP, BOAMP et TED Europa pour vous
                  proposer une <strong>veille intelligente</strong> et des <strong>recommandations IA</strong> basées
                  sur l'historique réel des attributions. Vous économisez des heures de recherche manuelle dans les
                  bases open data.
                </p>
              </div>
            </div>

            <div className="mt-12 p-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-2xl text-white text-center">
              <h3 className="text-2xl font-bold mb-4">Exploitez l'open data des marchés publics</h3>
              <p className="text-blue-100 mb-6">
                BOAMP, DECP, TED Europa : notre IA croise toutes ces sources pour vous proposer les meilleurs marchés.
              </p>
              <Link to="/register">
                <Button size="lg" className="bg-white text-blue-700 hover:bg-slate-100">
                  <Download className="w-5 h-5 mr-2" />
                  Essai gratuit 15 jours
                </Button>
              </Link>
            </div>
          </div>
        </article>

        <footer className="bg-slate-900 text-slate-400 py-8">
          <div className="container mx-auto px-4 text-center">
            <p>© 2026 Mon DCE Express - Tous droits réservés</p>
          </div>
        </footer>
      </div>
    </>
  );
}
